import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { passwordService } from '../services/api';

const ResetPassword: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { branding } = useBranding();

  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setTokenValid(false);
      return;
    }
    passwordService.validateToken(token)
      .then((res) => setTokenValid(res.valid))
      .catch(() => setTokenValid(false))
      .finally(() => setValidating(false));
  }, [token]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (novaSenha.length < 6) {
      setError('A senha deve ter no minimo 6 caracteres.');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setError('As senhas nao conferem.');
      return;
    }

    setLoading(true);
    try {
      await passwordService.resetPassword(token, novaSenha);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      const msg = err.response?.data?.message;
      setError(msg && typeof msg === 'string' ? msg : 'Erro ao redefinir senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (validating) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={32} />
          <Typography sx={{ mt: 2, fontSize: '0.875rem', color: '#696979' }}>
            Validando link...
          </Typography>
        </Box>
      );
    }

    if (!tokenValid) {
      return (
        <>
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px', fontSize: '0.875rem' }}>
            Link invalido ou expirado. Solicite um novo link de recuperacao.
          </Alert>
          <Box sx={{ textAlign: 'center' }}>
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/forgot-password')}
              sx={{
                fontSize: '0.875rem', fontWeight: 500, color: '#007AF0',
                textDecoration: 'none',
                '&:hover': { color: '#0069D0', textDecoration: 'underline' },
              }}
            >
              Solicitar novo link
            </Link>
          </Box>
        </>
      );
    }

    if (success) {
      return (
        <>
          <Alert severity="success" sx={{ mb: 2, borderRadius: '8px', fontSize: '0.875rem' }}>
            Senha redefinida com sucesso! Redirecionando para o login...
          </Alert>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        </>
      );
    }

    return (
      <>
        {error && (
          <Alert
            severity="error"
            onClose={() => setError('')}
            sx={{
              mb: 2, borderRadius: '8px',
              backgroundColor: '#FEECED', border: '1px solid #FAC8C9',
              fontSize: '0.875rem', color: '#A4151E',
            }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#515162', mb: 0.5 }}>
            Nova senha
          </Typography>
          <TextField
            required
            fullWidth
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            autoFocus
            placeholder="Minimo 6 caracteres"
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    sx={{ color: '#AAAAB9' }}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px', fontSize: '0.875rem',
                '& fieldset': { borderColor: '#D3D4DD' },
                '&:hover fieldset': { borderColor: '#007AF0' },
                '&.Mui-focused fieldset': { borderColor: '#007AF0' },
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#515162', mb: 0.5 }}>
            Confirmar senha
          </Typography>
          <TextField
            required
            fullWidth
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Repita a nova senha"
            value={confirmarSenha}
            onChange={(e) => setConfirmarSenha(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px', fontSize: '0.875rem',
                '& fieldset': { borderColor: '#D3D4DD' },
                '&:hover fieldset': { borderColor: '#007AF0' },
                '&.Mui-focused fieldset': { borderColor: '#007AF0' },
              },
            }}
          />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: 1.25, backgroundColor: '#007AF0',
            '&:hover': { boxShadow: 'none', backgroundColor: '#0069D0' },
            borderRadius: '8px', fontWeight: 500, fontSize: '0.875rem', boxShadow: 'none',
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Redefinir senha'}
        </Button>
      </>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom right, #002451, #0051A2, #007AF0)',
        p: 2,
      }}
    >
      {/* Logo */}
      <Box sx={{ textAlign: 'center', mb: 3, maxWidth: 384, width: '100%' }}>
        {branding.branding_logo ? (
          <Box
            component="img"
            src={branding.branding_logo}
            alt={branding.branding_nome}
            sx={{ width: 80, height: 80, objectFit: 'contain', borderRadius: '16px', mx: 'auto', mb: 2 }}
          />
        ) : (
          <Box
            sx={{
              width: 80, height: 80, mx: 'auto', mb: 2,
              backgroundColor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(4px)',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '1.875rem', fontWeight: 700, color: 'white' }}>
              {(branding.branding_nome || 'NG').substring(0, 2).toUpperCase()}
            </Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: '1.875rem', fontWeight: 700, color: 'white' }}>
          {branding.branding_nome || 'NeoGestor'}
        </Typography>
      </Box>

      {/* Card */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%', maxWidth: 384,
          backgroundColor: '#fff', borderRadius: '8px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          p: 4,
        }}
      >
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#24252E', textAlign: 'center', mb: 1 }}>
          Redefinir senha
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: '#696979', mb: 3, textAlign: 'center' }}>
          Crie uma nova senha para sua conta.
        </Typography>

        {renderContent()}

        {!success && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/login')}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                fontSize: '0.875rem', fontWeight: 500, color: '#007AF0',
                textDecoration: 'none',
                '&:hover': { color: '#0069D0', textDecoration: 'underline' },
              }}
            >
              <ArrowBack sx={{ fontSize: 16 }} />
              Voltar ao login
            </Link>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ResetPassword;
